import { createTheme } from '@mui/material/styles';

// The app keeps its own z-index scale in App.js, running from 1 to 9999:
//
//   9999  login modal (a hand-rolled Box, not a MUI Modal)
//   2000  anomalies card, simulated-data banner, expanded-panel scrim
//   1500  node info panel, sidebar
//   1490 / 1480 / 1450  mobile sidebar backdrop, floating hamburger, desktop backdrop
//   1400 / 1399  search bar, chip row, weather banner, filter chips, health banner
//   1100  panel shadow overlay
//
// MUI's default modal layer is 1300 and its tooltip layer is 1500. Both sit
// *below* most of that scale.
//
// Modal came first. Dialog, Menu, Popover and Select's dropdown all build on it,
// so every one of them rendered underneath the app's own chrome. On mobile that
// made the delete confirmation dialog invisible behind the full-width node
// panel — present and focusable, but appearing to do nothing.
//
// Tooltip is the same defect with a different symptom, found in #171: the help
// control on the anomalies card opened its tooltip *behind* the card it belongs
// to, because the card is at 2000 and a stock tooltip is at 1500. Help text
// rendered under the thing it explains is worse than no help — the control
// looks broken, and the reader learns not to press it.
//
// Until now there was no ThemeProvider at all, so nothing could be adjusted
// centrally. This theme exists to raise those two layers.
//
// Why not override zIndex per component: Select's dropdown is a Menu/Popover,
// which reads the same theme.zIndex.modal as the Dialog containing it. Both
// normally sit at 1300 and the dropdown wins the tie only because its portal is
// later in DOM order. Raising just the Dialog (`sx={{ zIndex: 2100 }}`) breaks
// that tie and pushes the dropdown behind its own dialog — this was tried and
// reverted. Moving the whole layer keeps every overlay on one level, so DOM
// order continues to decide, and nested popups stack correctly.
//
// 2100 clears the anomalies card and simulated banner (2000) and stays below
// the login modal (9999), which must remain on top of everything.
//
// 2300 for tooltip keeps MUI's own ordering: stock puts tooltip 200 above
// modal, and a tooltip opened from a control inside a dialog has to clear that
// dialog. The gap is preserved rather than re-invented, and it is still far
// below the login modal.
//
// Keep this theme minimal. Introducing a ThemeProvider makes this object the
// baseline for every MUI component in the app, all of which were written
// against stock defaults. Overriding anything beyond zIndex — palette, spacing,
// typography — would restyle the whole surface as a side effect. theme.test.js
// asserts that the two zIndex values are the only ones that differ from the
// default theme; restyling is a separate, deliberate decision.
const theme = createTheme({
  zIndex: { modal: 2100, tooltip: 2300 },
});

export default theme;
